import React from 'react';
import PropTypes from 'prop-types';
import colors from 'assets/theme/base/colors';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import SoftButton from 'components/SoftButton';
import TimelineList from 'examples/Timeline/TimelineList';
import TimelineItem from 'examples/Timeline/TimelineItem';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const AgencyNotesModal = ({ open, onClose, notes }) => {
  const { t } = useTranslation();

  const formatValue = (value) => {
    if (value === undefined || value === null) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    return value.toString();
  };

  const formatChange = (change) => {
    const { field, oldValue, newValue } = change;
    return `${field}: ${formatValue(oldValue)} → ${formatValue(newValue)}`;
  };

  const renderDescription = (note) => {
    try {
      const noteData = JSON.parse(note.noteText);
      return noteData.changes.map((change, index) => (
        <React.Fragment key={index}>
          {formatChange(change)}
          {index < noteData.changes.length - 1 && <br />}
        </React.Fragment>
      ));
    } catch {
      return note.noteText;
    }
  };

  const sortedNotes = [...notes].sort(
    (a, b) => dayjs(b.noteDate).valueOf() - dayjs(a.noteDate).valueOf(),
  );

  return (
    <Dialog
      sx={{
        '& .MuiPaper-root': {
          boxShadow: 'none',
          elevation: 0,
          maxWidth: '600px',
          backgroundColor: 'transparent',
        },
      }}
      fullWidth
      open={open}
    >
      <DialogTitle
        sx={{
          padding: '2.5rem 2rem 2rem 2rem',
          color: '#6c757d',
          fontWeight: '400',
          fontSize: '1.4625rem',
        }}
      >
        {t('AGENCY_MANAGER.AGENCY_NOTES.title')}
      </DialogTitle>
      <DialogContent sx={{ paddingTop: 0, maxHeight: '60vh' }}>
        <TimelineList>
          {sortedNotes.map((note) => (
            <TimelineItem
              key={note.identifier}
              color="light"
              icon="circle"
              title={(() => {
                try {
                  const noteData = JSON.parse(note.noteText);
                  return noteData.changeType;
                } catch {
                  return note.noteText;
                }
              })()}
              dateTime={dayjs(note.noteDate).format('MM/DD/YYYY HH:mm')}
              description={renderDescription(note)}
              badges={[]}
            />
          ))}
        </TimelineList>
      </DialogContent>
      <DialogActions>
        <SoftButton
          size="small"
          variant="outlined"
          color="secondary"
          onClick={onClose}
          sx={{
            color: '#5f5f5f',
            '&:hover': {
              borderColor: colors.info.main,
            },
          }}
        >
          {t('COMMON.close')}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};

AgencyNotesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      identifier: PropTypes.string.isRequired,
      noteText: PropTypes.string.isRequired,
      noteDate: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default AgencyNotesModal;
